import type {
  IssueCommentTimelineItem,
  ReviewCommentTimelineItem,
  ReviewTimelineItem,
  TimelineItem,
} from './timeline-types';
import type { PrV2CommentResponse, PrV2ReviewResponse } from './types';

import { TIMELINE_ITEM_TYPE } from './timeline-types';
import { asRecord } from './utils/as-record';

type DiscussionCommentTimelineItem = IssueCommentTimelineItem | ReviewCommentTimelineItem;
type ThreadCommentTimelineItem =
  | IssueCommentTimelineItem
  | ReviewCommentTimelineItem
  | ReviewTimelineItem;

function asNullableNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asNullableString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function getUpdatedAtGithub(item: object): string | null {
  const rawItem = asRecord(item);
  return asNullableString(rawItem?.updated_at);
}

function getGithubReviewId(
  item: DiscussionCommentTimelineItem | ThreadCommentTimelineItem,
): string | null {
  if (item.type !== 'review_comment') {
    return null;
  }

  return typeof item.pull_request_review_id === 'number'
    ? String(item.pull_request_review_id)
    : null;
}

function buildProjectedComment(
  item: DiscussionCommentTimelineItem | ThreadCommentTimelineItem,
  params: {
    line: number | null;
    position: number | null;
    inReplyToId: string | null;
  },
): PrV2CommentResponse {
  return {
    id: `synthetic-${item.type}-${item.id}`,
    githubCommentId: item.id,
    commentType:
      item.type === TIMELINE_ITEM_TYPE.ReviewComment
        ? TIMELINE_ITEM_TYPE.ReviewComment
        : TIMELINE_ITEM_TYPE.IssueComment,
    reactionSubjectType:
      item.type === TIMELINE_ITEM_TYPE.Review
        ? 'pull_request_review'
        : item.type === TIMELINE_ITEM_TYPE.ReviewComment
          ? TIMELINE_ITEM_TYPE.ReviewComment
          : TIMELINE_ITEM_TYPE.IssueComment,
    githubReviewId: getGithubReviewId(item),
    body: item.content ?? null,
    path: 'path' in item && typeof item.path === 'string' ? item.path : null,
    line: params.line,
    position: params.position,
    inReplyToId: params.inReplyToId,
    userLogin: item.user.login ?? null,
    userAvatarUrl: item.user.avatar_url ?? null,
    createdAtGithub: item.timestamp ?? null,
    updatedAtGithub: getUpdatedAtGithub(item),
    raw: item,
  };
}

export function buildPrV2CommentFromDiscussionTimelineItem(
  item: TimelineItem,
): PrV2CommentResponse | null {
  if (
    item.type !== TIMELINE_ITEM_TYPE.IssueComment &&
    item.type !== TIMELINE_ITEM_TYPE.ReviewComment
  ) {
    return null;
  }

  return buildProjectedComment(item, {
    line: 'line' in item && typeof item.line === 'number' ? item.line : null,
    position: 'position' in item && typeof item.position === 'number' ? item.position : null,
    inReplyToId:
      item.type === TIMELINE_ITEM_TYPE.ReviewComment &&
      'parentId' in item &&
      typeof item.parentId === 'string'
        ? item.parentId
        : null,
  });
}

export function buildPrV2ReviewFromDiscussionTimelineItem(
  item: TimelineItem,
): PrV2ReviewResponse | null {
  if (item.type !== TIMELINE_ITEM_TYPE.Review) {
    return null;
  }

  return {
    id: `synthetic-review-${item.id}`,
    githubReviewId: item.id,
    githubReviewNodeId: item.node_id ?? item.id,
    state: item.state ?? null,
    body: item.body ?? item.content ?? null,
    submittedAt: item.timestamp ?? item.created_at ?? null,
    userLogin: item.user.login ?? null,
    userAvatarUrl: item.user.avatar_url ?? null,
    commitId: item.commit_id ?? null,
    raw: item,
  };
}

export function buildPrV2CommentFromThreadTimelineItem(
  item: ThreadCommentTimelineItem,
): PrV2CommentResponse {
  // Thread rows have weaker source guarantees than the discussion snapshot, so we accept
  // broader line and reply-parent fallbacks here to keep an optimistic thread anchored even
  // when GitHub omits the most specific field on one of the intermediate snapshots.
  const line =
    'line' in item
      ? (asNullableNumber(item.line) ??
        asNullableNumber('original_line' in item ? item.original_line : null) ??
        asNullableNumber('position' in item ? item.position : null))
      : null;

  const inReplyToId =
    item.type === 'review_comment'
      ? typeof item.in_reply_to_id === 'number'
        ? String(item.in_reply_to_id)
        : null
      : (item.parentId ?? null);

  return buildProjectedComment(item, {
    line,
    position: 'position' in item ? asNullableNumber(item.position) : null,
    inReplyToId,
  });
}
